import { useMutation } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type {
  EmailTemplateDefaultModule,
  EmailTemplateDefaultQuerySchema,
} from '@/_pages/settings/email-template-defaults/shared/schemas';
import type { EmailTemplateQueryLinkerSchema } from '@/_pages/settings/email-templates/shared/schemas/email-templates-query-linker-schema';
import type { EmailTemplateQueryAllSchema } from '@/_pages/settings/email-templates/shared/schemas/email-templates-query-schema';
import type { UseControlledMutation } from '@/_schemas/types';
import { apiClient, queryClient, REFERENCE_DATA_QUERY_STALE_TIME_MS } from '@/_shared';

export function invoiceEmailTemplatesQueryKey(search = '', perPage = 100) {
  return ['EMAIL_TEMPLATES_ASYNC', 'invoice', { search, per_page: perPage }] as const;
}

export const INVOICE_EMAIL_TEMPLATES_STALE_TIME = REFERENCE_DATA_QUERY_STALE_TIME_MS;

export function getCachedInvoiceEmailTemplates(
  search = '',
  perPage = 100
): EmailTemplateQueryLinkerSchema[] | undefined {
  return queryClient.getQueryData(invoiceEmailTemplatesQueryKey(search, perPage));
}

export async function fetchInvoiceEmailTemplates(
  search = '',
  perPage = 100
): Promise<EmailTemplateQueryLinkerSchema[]> {
  return queryClient.fetchQuery({
    queryKey: invoiceEmailTemplatesQueryKey(search, perPage),
    staleTime: INVOICE_EMAIL_TEMPLATES_STALE_TIME,
    queryFn: async () => {
      const res = await apiClient.get<EmailTemplateQueryAllSchema>({
        endpoint: '/api/v1/email-templates',
        params: { search, per_page: perPage, 'filter[email_type]': 'invoice' },
      });
      const data = res?.data ?? [];
      const linkers: EmailTemplateQueryLinkerSchema[] = data
        .filter((t) => t.email_type === 'invoice')
        .map((t) => ({ id: t.id, name: t.name }));

      return orderBy(linkers, ['name']);
    },
  });
}

export function resolveInvoiceEmailDefaultTemplate(
  module: EmailTemplateDefaultModule,
  defaults: EmailTemplateDefaultQuerySchema[],
  templates: EmailTemplateQueryLinkerSchema[]
): EmailTemplateQueryLinkerSchema | null {
  const defaultRow = defaults.find((row) => row.module === module);
  if (!defaultRow?.email_template_id) {
    return null;
  }

  const fromList = templates.find((template) => template.id === defaultRow.email_template_id);
  if (fromList) {
    return fromList;
  }

  if (defaultRow.email_template) {
    return { id: defaultRow.email_template.id, name: defaultRow.email_template.name };
  }

  return null;
}

export const useGetInvoiceEmailTemplatesMutation: UseControlledMutation<EmailTemplateQueryLinkerSchema> = (
  params = {}
) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => fetchInvoiceEmailTemplates(search ?? '', per_page ?? 100),
  });
